import { createActionGroup, emptyProps, props, union } from '@ngrx/store';
import { INormalizedPayload } from '@core/state/entities/entity';
import {
  FullStatsKeys,
  IStudyOutgoingDto,
} from '@core/services/stage/review-effects';
import {
  StudyEntityKeys,
  studySchema,
} from '../state/entities/study/study.entity';
import {
  ReviewerAnnotationStats,
  ReviewStatus,
  StudyReviewAccessResult,
  StudySummaryDto,
} from '@core/services/api-client.generated';
import {
  ISession,
  SessionEntityKeys,
} from '@core/state/entities/session/session.entity';
import { IScreening } from '@core/state/entities/screening/screening.entity';
import { pick } from 'lodash-es';
import {
  IErrorStateWithCorrelation,
  IRequestCorrelationState,
} from '@core/actions/project-detail.actions';
import {
  ReviewerAnnotationStatsKey,
  ReviewerScreeningStatsKey,
  ScreeningEntityKey,
} from '@core/state/entities/entity-name-map';

export interface IIncompleteStudiesPayload {
  investigatorId: string;
  stageId: string;
  incompleteStudies: StudySummaryDto[];
  currentStudyId: string | null;
}

export const incompleteStudiesPayloadSchema = {
  incompleteStudies: [studySchema],
};

export const stageActions = createActionGroup({
  source: 'Stage',
  events: {
    'retrieve stage reviewer stats': props<
      { projectId: string; stageId: string } & IRequestCorrelationState
    >(),
    'stage reviewer stats delegated': emptyProps(),
    'received stage reviewer stats': props<
      INormalizedPayload<
        ReviewerScreeningStatsKey | ReviewerAnnotationStatsKey
      > & { stageId: string; projectId: string } & IRequestCorrelationState
    >(),
    'error retrieving stage reviewer stats': props<
      IErrorStateWithCorrelation & { stageId: string }
    >(),
    'retrieve full stats': props<
      {
        projectId: string;
        stageId: string;
      } & IRequestCorrelationState
    >(),
    'received full stats': props<
      INormalizedPayload<FullStatsKeys> & {
        signalRUpdate: boolean;
      }
    >(),
    'error retrieving full stats': props<IErrorStateWithCorrelation>(),
    'retrieve in progress studies': props<
      {
        projectId: string;
        stageId: string;
        reconciliation: boolean;
      } & IRequestCorrelationState
    >(),
    'received in progress studies': props<
      INormalizedPayload<
        SessionEntityKeys | StudyEntityKeys | ScreeningEntityKey
      > &
        IRequestCorrelationState
    >(),
    'error retrieving in progress studies': props<IErrorStateWithCorrelation>(),
    'review navigation complete': emptyProps(),
    'study review access result received': props<{
      result: StudyReviewAccessResult;
      projectId: string;
      stageId: string;
    }>(),
    /**
     * D8 decision 2: the server released the reviewer's leftover annotation claim on one of their own studies (no
     * longer allocated to them, and no place left). Carries only that study's id.
     */
    'annotation claim released': props<{
      projectId: string;
      stageId: string;
      studyId: string;
      /** From the targeted event (#3720); absent when a response reported the release. */
      reason?: 'AllocationNotAssigned' | 'SettingsChanged';
      activities?: ('Screening' | 'Annotation')[];
    }>(),
    'session saved': props<
      INormalizedPayload<SessionEntityKeys> & {
        projectId: string;
        stageId: string;
      } & Partial<IRequestCorrelationState>
    >(),
    'update session': props<
      INormalizedPayload<SessionEntityKeys, { session: ISession }> & {
        sessionId: string;
      }
    >(),
    'max saved sessions reached': emptyProps(),
    'restrict to new sessions': emptyProps(),
    'restrict to saved study sessions': emptyProps(),

    'get next study for stage': props<{
      stageId: string;
      projectId: string;
      restrictToSaved: boolean;
      reconciliation: boolean;
    }>(),
    /**
     * Revision-2 §4.3: record the decision and stay on the study.
     *
     * Deliberately distinct from `submit screening and next study`, whose
     * endpoint also allocates and reserves the next study. A reviewer who
     * stays must reserve nothing.
     */
    'submit screening without advancing': props<{
      screening: IScreening;
      /**
       * Identifies this decision uniquely, across every review the browser
       * opens. A failure only rolls back if it is still the reviewer's latest
       * intent; an older one has already been superseded, and one from another
       * review instance can never match at all.
       */
      intentId: string;
      /**
       * The last decision the SERVER acknowledged for this study, or `null` if
       * it has never accepted one. This is the only value a rollback restores:
       * a baseline read from optimistic state can itself be a decision that
       * was never stored.
       */
      confirmedScreening: IScreening | null;
    }>(),
    /** The server accepted the decision; it becomes the confirmed baseline. */
    'submit screening without advancing success': props<{
      screening: IScreening;
      /** Echoed so a listener can tell which decision this answers. */
      intentId: string;
    }>(),
    /** Rolls the optimistic decision back to the confirmed baseline. */
    'submit screening without advancing failure': props<{
      screening: IScreening;
      intentId: string;
      /** The last server-acknowledged decision for this study, or none. */
      confirmedScreening: IScreening | null;
    }>(),
    'submit screening and next study': props<{
      screening: IScreening;
      reconciliation: boolean;
      restrictToSaved: boolean;
      /** As above: the confirmed state to restore if the write is refused. */
      confirmedScreening?: IScreening | null;
      intentId?: string;
    }>(),
    /** The server accepted the decision; it becomes the confirmed baseline. */
    'submit screening and next study success': props<{
      screening: IScreening;
      intentId?: string;
    }>(),
    /**
     * Rolls the optimistic decision back to the confirmed baseline. Nothing
     * persisted and no next study loaded, so the reviewer is still looking at
     * this study and must not see a decision on it.
     */
    'submit screening and next study failure': props<{
      screening: IScreening;
      intentId?: string;
      confirmedScreening: IScreening | null;
    }>(),
    'incomplete sessions updated': props<IIncompleteStudiesPayload>(),
    'save session': props<
      IStudyOutgoingDto & Partial<IRequestCorrelationState>
    >(),
    'revert session': props<
      {
        stageId: string;
        studyId: string;
        projectId: string;
        reconciliation: boolean;
      } & Partial<IRequestCorrelationState>
    >(),
    'delete session': props<
      IStudyOutgoingDto & Partial<IRequestCorrelationState>
    >(),
    'commit session deletion': props<
      IStudyOutgoingDto & IRequestCorrelationState
    >(),
    'annotation form persistence succeeded': props<
      IRequestCorrelationState & { operation: 'save' | 'delete' | 'revert' }
    >(),
    'annotation form persistence failed': props<
      IErrorStateWithCorrelation & {
        operation: 'save' | 'delete' | 'revert';
      }
    >(),
    'load stage study': props<{
      stageId: string;
      studyId: string;
      projectId: string;
      reconciliation: boolean;
    }>(),
  },
});
export const stageStudyActions = createActionGroup({
  source: 'Stage Study Received',
  events: {
    'received stage study': props<
      INormalizedPayload<StudyEntityKeys> & {
        studyId: string;
        reviewStatus: ReviewStatus;
      }
    >(),
    'no new studies for stage': props<{ reviewStatus: ReviewStatus }>(),
    'error receiving next study': props<{ error: any }>(),
  },
});
const studyReceivedAction = union(stageStudyActions);
export type StudyReceivedAction = typeof studyReceivedAction;
